import { Link } from 'react-router-dom';
import { FileText, Lock } from 'lucide-react';
import { formatDate } from '@/utils';
import type { ArticleListItem } from '@/types';

interface ArticleRowProps {
  article: ArticleListItem;
  categorySlug?: string;
  sectionSlug?: string;
  /**
   * Title-only row. Long sections (see `LONG_SECTION` in SectionPage) drop the
   * excerpt and the updated date so the list stays scannable — at ~110px per
   * full row, 60 articles is 7,000px of scroll nobody reads. The title itself
   * still wraps rather than truncating: it is the only thing left to scan, and
   * on a phone most help-center titles are wider than the screen.
   */
  compact?: boolean;
}

export function ArticleRow({ article, categorySlug, sectionSlug, compact }: ArticleRowProps) {
  const isRestricted = article.visibility === 'signed_in';
  const href = categorySlug && sectionSlug
    ? `/${categorySlug}/${sectionSlug}/${article.slug}`
    : `/articles/${article.slug}`;

  if (compact) {
    return (
      <Link
        to={href}
        className="group flex items-start gap-3 px-4 py-3 rounded-lg border bg-card hover:border-primary/50 hover:shadow-sm transition-all"
      >
        {/* No leading file icon here, unlike the full row: every article in the
            list would carry the same one, and in a title-only row it competes
            with the lock — the one mark that actually says something. */}
        <h3 className="min-w-0 flex-1 font-medium text-foreground group-hover:text-primary transition-colors">
          {article.title}
        </h3>
        {isRestricted && (
          // h-6 is the title's line box (16px text at Tailwind's 1.5), so the
          // lock sits on the middle of the FIRST line rather than drifting down
          // with a title that wraps.
          <span className="flex h-6 flex-shrink-0 items-center">
            <Lock className="h-3.5 w-3.5 text-muted-foreground" />
          </span>
        )}
      </Link>
    );
  }

  return (
    <Link
      to={href}
      className="group flex items-start gap-4 p-4 rounded-lg border bg-card hover:border-primary/50 hover:shadow-sm transition-all"
    >
      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-muted flex items-center justify-center mt-0.5">
        <FileText className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-foreground group-hover:text-primary transition-colors">
            {article.title}
          </h3>
          {isRestricted && (
            <Lock className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          )}
        </div>
        {article.excerpt && (
          <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
            {article.excerpt}
          </p>
        )}
        <p className="mt-2 text-xs text-muted-foreground">
          Updated {formatDate(article.updated_at)}
        </p>
      </div>
    </Link>
  );
}
